import {TextField, Field, type SectionProps} from '../controls';
import {retargetSiteLinks} from '@common/enrollmentSite/siteLinks';
import {Input} from '@common-web/components/ui/input';
import {Button} from '@common-web/components/ui/button';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {Info} from 'lucide-react';
import {SiteWordmark} from '@client/pages/public/enroll/components/SiteWordmark';
import {AssetUploadButton, MAX_LOGO_BYTES} from '../AssetUploadButton';

export function BrandSection({config, patch, slugLocked, groupSlug, isNew}: SectionProps) {
  return (
    <div className='space-y-5'>
      <Field
        label='URL slug'
        hint={
          slugLocked
            ? `Locked — the site is live at /${config.slug}. Changing the URL would break the live link and any shared links.`
            : `The site lives at /${config.slug || 'slug'}. Lowercase letters, numbers, and hyphens. This locks once the site is published.`
        }
      >
        <Input
          value={config.slug}
          disabled={slugLocked}
          placeholder='client-name'
          onChange={(e) => {
            const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
            // Keep landing cards that point at this site's own pages on the new slug.
            patch({
              slug,
              ...(config.resourcesSection
                ? {resourcesSection: retargetSiteLinks(config.resourcesSection, config.slug, slug)}
                : {}),
            });
          }}
        />
      </Field>
      <TextField
        label='Site name'
        hint='Browser tab title, logo alt text, and the name on every submission.'
        value={config.name}
        onChange={(name) => patch({name})}
      />
      <Field
        label='Logo'
        hint='Shown in the site header. Leave blank to show the site name instead.'
        labelAccessory={<LogoSpecsInfo />}
      >
        <div className='space-y-2'>
          {config.logoUrl ? (
            <img src={config.logoUrl} alt='' className='h-10 w-auto rounded border bg-white p-1' />
          ) : (
            <div className='rounded-lg border bg-white p-3'>
              <SiteWordmark name={config.name || 'Your site'} primaryColor={config.primaryColor} />
            </div>
          )}
          <div className='flex items-center gap-2'>
            <AssetUploadButton
              groupSlug={groupSlug}
              kind='logo'
              accept='image/png,image/svg+xml,image/jpeg,image/webp'
              maxBytes={MAX_LOGO_BYTES}
              disabled={isNew}
              label={config.logoUrl ? 'Replace' : 'Upload logo'}
              onUploaded={(logoUrl) => patch({logoUrl})}
            />
            {config.logoUrl && (
              <Button variant='outline' size='sm' onClick={() => patch({logoUrl: ''})}>
                Remove
              </Button>
            )}
          </div>
          {isNew && (
            <p className='text-xs text-muted-foreground'>Save the site first to upload a logo.</p>
          )}
        </div>
      </Field>
      <Field label='Primary color' hint='The one themeable brand token — drives buttons, icons, and accent text.'>
        <div className='flex items-center gap-2'>
          <input
            type='color'
            value={config.primaryColor || '#1F3A5F'}
            onChange={(e) => patch({primaryColor: e.target.value})}
            className='h-9 w-12 cursor-pointer rounded border'
          />
          <Input
            value={config.primaryColor}
            onChange={(e) => patch({primaryColor: e.target.value})}
            placeholder='#1F3A5F'
            className='max-w-[10rem]'
          />
        </div>
      </Field>
      <TextField
        label='Banner'
        hint='Optional full-width strip under the header on the landing and gate screens. Blank hides it.'
        value={config.banner}
        onChange={(banner) => patch({banner: banner || undefined})}
      />
    </div>
  );
}

// Recommended-specs popover on the Logo label. The header renders the logo ~48px tall,
// so upload larger for a crisp result. Max size mirrors the client cap (MAX_LOGO_BYTES).
function LogoSpecsInfo() {
  const specs: [string, string][] = [
    ['Format', 'SVG or transparent PNG (avoid JPG)'],
    ['Dimensions', 'At least 150px tall, wide shapes work best'],
    ['File size', `Aim under 200 KB · max ${Math.round(MAX_LOGO_BYTES / 1024 / 1024)} MB`],
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label='Logo guidelines'
          className='text-muted-foreground transition-colors hover:text-foreground'
        >
          <Info className='h-3.5 w-3.5' />
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-72 space-y-2 text-xs'>
        <p className='font-medium text-foreground'>Logo guidelines</p>
        <ul className='space-y-1.5'>
          {specs.map(([k, v]) => (
            <li key={k} className='flex flex-col'>
              <span className='font-medium text-foreground'>{k}</span>
              <span className='mt-0.5 text-muted-foreground'>{v}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
